/**
 * Wishlist Module for Shopify
 * Handles wishlist functionality with customer metafields (logged-in) and localStorage (guest)
 * November 2025
 */

class WishlistManager {
  constructor() {
    this.storageKey = 'argent_asher_wishlist';
    this.metafieldNamespace = 'wishlist';
    this.metafieldKey = 'items';
    this.customerId = window.Shopify?.customerAccessToken ? this.getCustomerId() : null;
    this.wishlistItems = [];
    this.initialized = false;
    
    this.init();
  }

  /**
   * Initialize wishlist from localStorage
   * Note: Cross-device sync requires a backend/app proxy - currently using localStorage only
   */
  async init() {
    if (this.initialized) return;
    
    try {
      // Always use localStorage - cross-device sync requires Admin API backend
      this.loadFromLocalStorage();
      
      this.initialized = true;
      this.updateAllButtons();
      this.updateWishlistCount();
      
      // Dispatch custom event for wishlist loaded
      document.dispatchEvent(new CustomEvent('wishlist:loaded', { 
        detail: { items: this.wishlistItems } 
      }));
    } catch (error) {
      console.error('Wishlist initialization error:', error);
      this.wishlistItems = [];
    }
  }

  /**
   * Check if customer is logged in
   */
  isCustomerLoggedIn() {
    // Check if customer object exists in window
    return typeof window.Shopify?.customer !== 'undefined' && window.Shopify.customer;
  }

  /**
   * Get customer ID from Shopify
   */
  getCustomerId() {
    return window.Shopify?.customer?.id || null;
  }

  /**
   * Load wishlist from localStorage (guest users)
   */
  loadFromLocalStorage() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      this.wishlistItems = stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading wishlist from localStorage:', error);
      this.wishlistItems = [];
    }
  }

  /**
   * Save wishlist to localStorage (guest users)
   */
  saveToLocalStorage() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlistItems));
    } catch (error) {
      console.error('Error saving wishlist to localStorage:', error);
    }
  }

  /**
   * Load wishlist from customer metafields (logged-in users)
   */
  async loadFromMetafields() {
    if (!this.isCustomerLoggedIn()) {
      this.loadFromLocalStorage();
      return;
    }

    try {
      const customerId = this.getCustomerId();
      if (!customerId) {
        this.loadFromLocalStorage();
        return;
      }

      const query = `
        query getCustomerWishlist($customerId: ID!) {
          customer(id: $customerId) {
            id
            metafield(namespace: "${this.metafieldNamespace}", key: "${this.metafieldKey}") {
              value
            }
          }
        }
      `;

      const response = await this.storefrontApiRequest(query, { customerId: `gid://shopify/Customer/${customerId}` });
      
      if (response?.data?.customer?.metafield?.value) {
        this.wishlistItems = JSON.parse(response.data.customer.metafield.value);
      } else {
        // Check localStorage for guest wishlist to merge
        this.loadFromLocalStorage();
        if (this.wishlistItems.length > 0) {
          await this.saveToMetafields(); // Merge guest wishlist
        }
      }
    } catch (error) {
      console.warn('Storefront API not available, using localStorage only:', error.message);
      // Mark API as unavailable to skip future metafield operations
      this.apiUnavailable = true;
      // Silently fall back to localStorage - this is expected if API access is not configured
      this.loadFromLocalStorage();
    }
  }

  /**
   * Save wishlist to customer metafields (logged-in users)
   */
  async saveToMetafields() {
    if (!this.isCustomerLoggedIn()) {
      this.saveToLocalStorage();
      return;
    }

    // If API failed during load, skip saving to metafields
    if (this.apiUnavailable) {
      this.saveToLocalStorage();
      return;
    }

    try {
      const customerId = this.getCustomerId();
      if (!customerId) {
        this.saveToLocalStorage();
        return;
      }

      const mutation = `
        mutation updateCustomerWishlist($input: CustomerUpdateInput!) {
          customerUpdate(input: $input) {
            customer {
              id
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const input = {
        id: `gid://shopify/Customer/${customerId}`,
        metafields: [{
          namespace: this.metafieldNamespace,
          key: this.metafieldKey,
          value: JSON.stringify(this.wishlistItems),
          type: 'json'
        }]
      };

      await this.storefrontApiRequest(mutation, { input });
      
      // Also save to localStorage as backup
      this.saveToLocalStorage();
    } catch (error) {
      console.error('Error saving wishlist to metafields:', error);
      this.saveToLocalStorage(); // Fallback
    }
  }

  /**
   * Make Storefront API request
   */
  async storefrontApiRequest(query, variables = {}) {
    const endpoint = `${window.Shopify.routes.root}api/${window.Shopify.apiVersion || '2024-10'}/graphql.json`;
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.Shopify.storefrontAccessToken || ''
      },
      body: JSON.stringify({ query, variables })
    });

    if (!response.ok) {
      throw new Error(`Storefront API request failed: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Add product to wishlist
   */
  async addToWishlist(productId, productHandle, productTitle, productImage, productPrice) {
    const itemId = String(productId);
    
    // Check if already in wishlist
    if (this.wishlistItems.some(item => item.id === itemId)) {
      console.log('Product already in wishlist');
      return false;
    }

    const wishlistItem = {
      id: itemId,
      handle: productHandle,
      title: productTitle,
      image: productImage,
      price: productPrice,
      addedAt: new Date().toISOString()
    };

    this.wishlistItems.push(wishlistItem);
    
    // Save to localStorage (cross-device sync requires backend)
    this.saveToLocalStorage();

    this.updateAllButtons();
    this.updateWishlistCount();
    
    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('wishlist:added', { 
      detail: { item: wishlistItem } 
    }));

    return true;
  }

  /**
   * Remove product from wishlist
   */
  async removeFromWishlist(productId) {
    const itemId = String(productId);
    const initialLength = this.wishlistItems.length;
    
    this.wishlistItems = this.wishlistItems.filter(item => item.id !== itemId);
    
    if (this.wishlistItems.length === initialLength) {
      console.log('Product not found in wishlist');
      return false;
    }

    // Save to localStorage (cross-device sync requires backend)
    this.saveToLocalStorage();

    this.updateAllButtons();
    this.updateWishlistCount();
    
    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('wishlist:removed', { 
      detail: { productId: itemId } 
    }));

    return true;
  }

  /**
   * Check if product is in wishlist
   */
  isInWishlist(productId) {
    return this.wishlistItems.some(item => item.id === String(productId));
  }

  /**
   * Get all wishlist items
   */
  getWishlistItems() {
    return [...this.wishlistItems];
  }

  /**
   * Get wishlist count
   */
  getWishlistCount() {
    return this.wishlistItems.length;
  }

  /**
   * Clear entire wishlist
   */
  async clearWishlist() {
    this.wishlistItems = [];
    
    // Save to localStorage (cross-device sync requires backend)
    this.saveToLocalStorage();

    this.updateAllButtons();
    this.updateWishlistCount();
    
    document.dispatchEvent(new CustomEvent('wishlist:cleared'));
  }

  /**
   * Update all wishlist buttons on the page
   */
  updateAllButtons() {
    const buttons = document.querySelectorAll('[data-wishlist-button]');
    buttons.forEach(button => {
      const productId = button.getAttribute('data-product-id');
      if (productId) {
        this.updateButton(button, this.isInWishlist(productId));
      }
    });
  }

  /**
   * Update single wishlist button state
   */
  updateButton(button, isInWishlist) {
    const icon = button.querySelector('.wishlist-icon');
    const text = button.querySelector('.wishlist-text');
    
    if (isInWishlist) {
      button.classList.add('is-active');
      button.setAttribute('aria-pressed', 'true');
      if (icon) icon.classList.add('is-filled');
      if (text) text.textContent = button.getAttribute('data-remove-text') || 'Remove from Wishlist';
    } else {
      button.classList.remove('is-active');
      button.setAttribute('aria-pressed', 'false');
      if (icon) icon.classList.remove('is-filled');
      if (text) text.textContent = button.getAttribute('data-add-text') || 'Add to Wishlist';
    }
  }

  /**
   * Update wishlist count in header
   */
  updateWishlistCount() {
    const countElements = document.querySelectorAll('[data-wishlist-count]');
    const count = this.getWishlistCount();
    
    countElements.forEach(element => {
      element.textContent = count;
      element.classList.toggle('is-visible', count > 0);
    });
  }

  /**
   * Toggle wishlist item
   */
  async toggleWishlist(productId, productHandle, productTitle, productImage, productPrice) {
    if (this.isInWishlist(productId)) {
      return await this.removeFromWishlist(productId);
    } else {
      return await this.addToWishlist(productId, productHandle, productTitle, productImage, productPrice);
    }
  }
}

// Initialize wishlist manager after DOM is ready
let wishlistManager;

function initWishlist() {
  if (wishlistManager) return; // Already initialized
  wishlistManager = new WishlistManager();
  window.wishlistManager = wishlistManager;
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWishlist);
} else {
  // DOM already loaded
  initWishlist();
}

// Handle wishlist button clicks
document.addEventListener('click', async (event) => {
  if (!wishlistManager) return; // Not initialized yet
  const button = event.target.closest('[data-wishlist-button]');
  if (!button) return;

  event.preventDefault();
  
  const productId = button.getAttribute('data-product-id');
  const productHandle = button.getAttribute('data-product-handle');
  const productTitle = button.getAttribute('data-product-title');
  const productImage = button.getAttribute('data-product-image');
  const productPrice = button.getAttribute('data-product-price');

  if (!productId) {
    console.error('Product ID is required for wishlist button');
    return;
  }

  // Add loading state
  button.classList.add('is-loading');
  button.disabled = true;

  try {
    const isInWishlist = wishlistManager.isInWishlist(productId);
    
    if (isInWishlist) {
      await wishlistManager.removeFromWishlist(productId);
      
      // Show notification
      if (window.showNotification) {
        window.showNotification('Removed from wishlist', 'notice');
      }
    } else {
      await wishlistManager.addToWishlist(productId, productHandle, productTitle, productImage, productPrice);
      
      // Show notification
      if (window.showNotification) {
        window.showNotification('Added to wishlist', 'success');
      }
    }
  } catch (error) {
    console.error('Error toggling wishlist:', error);
    if (window.showNotification) {
      window.showNotification('Error updating wishlist', 'error');
    }
  } finally {
    button.classList.remove('is-loading');
    button.disabled = false;
  }
});

// Export for use in other modules
window.WishlistManager = WishlistManager;

// Reinitialize on Shopify section re-render
if (document.documentElement.hasAttribute('data-theme-editor')) {
  document.addEventListener('shopify:section:load', () => {
    if (wishlistManager) {
      wishlistManager.updateAllButtons();
      wishlistManager.updateWishlistCount();
    }
  });
}
